import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { useGTMDataLayer } from './GTM/useGTMDataLayer';
import useSelector from '../redux/typedHooks';
import { NAFGTMEvent } from '../../../types/GTM/NAFGTMEvent';
import { globalTabState } from './useActiveTab';

export const useVirtualPageViewTracking = () => {
  const location = useLocation();
  const dataLayer = useGTMDataLayer();
  const [currentTitle, setCurrentTitle] = useState<undefined | string>();
  const baseUrl = useSelector((state) => state.application.baseUrl);
  const user = useSelector((state) => state.myMembership.customerInformation.data);
  const applicationInsights = useAppInsightsContext();
  const { activeTabTitle } = globalTabState;

  // Track if we've already sent tracking for this URL
  const hasTrackedRef = useRef<{
    url: string;
    hasTracked: boolean;
  }>({ url: '', hasTracked: false });

  const fullPageTitle = activeTabTitle ? `${currentTitle || ''} - ${activeTabTitle}` : currentTitle;

  // User data tracking
  useEffect(() => {
    if (dataLayer && user?.customer && user.membership && currentTitle && currentTitle !== '') {
      dataLayer.push({
        // eslint-disable-next-line camelcase
        user_id: user.customerId,
        userType: user.customer.displayRoleName as string,
        hasMembership: user.membership.isValid,
        isMainMember: user.customer.customerRoleName === 'MainHouseHoldMember',
        isHouseMember: user.customer.customerRoleName !== 'MainHouseHoldMember',
        hasRoadSideAssistance: !!user.membership.roadSideAssistance,
      });
    }
  }, [user, dataLayer, currentTitle]);

  useEffect(() => {
    if (!dataLayer || !baseUrl || !currentTitle || currentTitle === '') {
      return;
    }

    const currentUrl = `${location.pathname}${location.search}`;

    // If URL changed, reset tracking state
    if (currentUrl !== hasTrackedRef.current.url) {
      hasTrackedRef.current = { url: currentUrl, hasTracked: false };
    }

    if (hasTrackedRef.current.hasTracked && !activeTabTitle) {
      return;
    }

    hasTrackedRef.current.hasTracked = true;

    // Track the page view
    dataLayer.push({
      event: NAFGTMEvent.virtualPageView,
      page: location.pathname,
      pageUrl: `${baseUrl}${location.pathname}${location.search}`,
      pageTitle: fullPageTitle || 'UNKNOWN',
    });

    // Also track with Application Insights
    if (applicationInsights) {
      applicationInsights.trackPageView({
        name: fullPageTitle || 'UNKNOWN',
        uri: `${baseUrl}${location.pathname}${location.search}`,
        properties: {
          tabName: activeTabTitle || null,
        },
      });
    }
  }, [
    currentTitle,
    activeTabTitle,
    dataLayer,
    baseUrl,
    location.pathname,
    location.search,
    fullPageTitle,
    applicationInsights,
  ]);

  // Title observer effect
  useEffect(() => {
    if (typeof document !== 'undefined' && !!document) {
      const observer = new MutationObserver((mutations: MutationRecord[]) => {
        if (mutations[0].target.textContent) {
          setCurrentTitle(mutations[0].target.textContent);
        }
      });
      const titleElement = document.querySelector('title');
      if (titleElement) {
        observer.observe(titleElement, {
          subtree: true,
          characterData: true,
          childList: true,
        });
        setCurrentTitle(document.title);
        return () => observer.disconnect();
      }
      setCurrentTitle(document.title);
    }
    return () => {};
  }, []);
};
